import React, { useState } from 'react';

import { DownloadCloud, EyeIcon } from 'lucide-react';

import { formatDate } from '../../../dateFormat';

import {
  AdvancedTable,
  Button,
  Checkbox,
  useIsMobile,
} from '@sandtech-hub-monorepo/ui-kit';
import { GetProgramEnrollmentPaymentPlansQuery } from '@sandtech-hub-monorepo/ehub-client';
import { downloadFromUrl } from '../../../../utils';
import { ReactComponent as PDFIcon } from '../../../assets/Icons/pdf-icon.svg';

type Payment = NonNullable<
  NonNullable<
    NonNullable<GetProgramEnrollmentPaymentPlansQuery['viewer']>['paymentPlans']
  >[number]['payments'][number]
>;

interface Props {
  payments: Payment[];
}

export const ReceiptsSection = ({ payments }: Props) => {
  const mobile = useIsMobile();
  const [paymentDownloadUrls, setPaymentDownloadUrls] = useState<string[]>([]);

  return (
    <>
      <h2 className="text-sm mb-6 flex justify-between items-center mt-6">
        Receipts
        {payments?.length ? (
          <Button
            variant="outline"
            className="gap-1.5 hidden sm:flex"
            onClick={() =>
              payments
                ?.filter(({ downloadReceiptUrl }) =>
                  paymentDownloadUrls.includes(downloadReceiptUrl!)
                )
                .forEach(({ downloadReceiptUrl, id, name }) =>
                  downloadFromUrl(
                    'Receipt ' + name + ' #' + id + '.pdf',
                    downloadReceiptUrl!
                  )
                )
            }
          >
            <DownloadCloud /> Download
          </Button>
        ) : null}
      </h2>
      {payments?.length ? (
        <AdvancedTable
          pageSize={6}
          entries={payments}
          columns={[
            mobile
              ? null
              : {
                  key: 'select',
                  name: (
                    <Checkbox
                      checked={payments.length === paymentDownloadUrls.length}
                      onCheckedChange={(checked) =>
                        setPaymentDownloadUrls(
                          checked
                            ? payments
                                .map(
                                  ({ downloadReceiptUrl }: Payment) =>
                                    downloadReceiptUrl!
                                )
                                .filter(Boolean)
                            : []
                        )
                      }
                    />
                  ),
                  cell: ({ downloadReceiptUrl }: Payment) =>
                    downloadReceiptUrl && (
                      <Checkbox
                        checked={paymentDownloadUrls.includes(
                          downloadReceiptUrl
                        )}
                        onCheckedChange={(checked) =>
                          setPaymentDownloadUrls((urls) =>
                            checked
                              ? [...urls, downloadReceiptUrl]
                              : urls.filter((u) => u !== downloadReceiptUrl)
                          )
                        }
                      />
                    ),
                },
            {
              key: 'receipt',
              name: 'Receipt',
              cell: ({ name, id }: Payment) => (
                <div className="flex gap-3 items-center">
                  <PDFIcon className="hidden sm:block" /> Receipt {name} #{id}
                </div>
              ),
            },
            {
              key: 'paymentDate',
              name: 'Payment Date',
              cell: ({ paidAt }: Payment) => (
                <span className="text-tertiary">
                  {formatDate(paidAt)}
                </span>
              ),
              sortValue: ({ paidAt }: Payment) =>
                new Date(paidAt || '').valueOf(),
            },
            {
              key: 'amount',
              name: 'Amount',
              cell: ({ amount }: Payment) => (
                <span className="text-tertiary">
                  USD {(amount / 100).toFixed(2)}
                </span>
              ),
            },
            mobile
              ? null
              : {
                  key: 'actions',
                  name: '',
                  cell: ({ viewReceiptUrl }: Payment) => (
                    <a
                      href={viewReceiptUrl || ''}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <EyeIcon className="text-primary cursor-pointer" />
                    </a>
                  ),
                },
          ]}
        />
      ) : (
        <div className="text-sm text-muted-foreground">
          Your receipts will show here once you make at least one payment
        </div>
      )}
    </>
  );
};
